// rename-table / move-table-schema edit planner (v2 Slice D1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D1.
//
// Both ops append a fresh statement after the source statement (DSE2-7: no
// coalescing by default). The source statement kind is unrestricted — the
// edit is independent of the source DDL because the synthesized statement
// names the target table directly via `edit.fromTable`.
//
// One exception: when the source statement IS the `CREATE TABLE` of the table
// being renamed / moved, the declaration itself is rewritten. That is the
// Table Designer's case — its buffer is a declaration of the table's state,
// and an appended `RENAME` beside a declaration that still spells the old
// name left everything typed below it, and every later save, naming a table
// that no longer existed (PR #76 review, codex round 9 / Fable rounds 12–13).
// The appended form is always terminated with `;`: T-SQL needs none, but the
// designer reads the buffer as statements and an unterminated `EXEC sp_rename`
// fused with whatever was typed on the next line.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { DdlEdit, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { maybeQuoteIdent } from './dialect';
import { needsQuoting } from './dialect.quoting';
import {
  synthesizeMoveTableSchema,
  synthesizeRenameTable,
} from './dialect.tableLifecycle';

type TableLifecycleEdit = Extract<
  DdlEdit,
  { op: 'rename-table' | 'move-table-schema' }
>;

export function planTableLifecycleEdit(
  ctx: DdlEditCtx,
  edit: TableLifecycleEdit,
): readonly PlannedByteEdit[] {
  // A schema move is synthesized first even on the declaration path: this is
  // where an engine that has no such op (SQLite) throws its hint. A rename's
  // declaration rewrite needs no engine check — every engine renames a table,
  // and Oracle's bare-name rule concerns its RENAME statement only, not
  // `ALTER TABLE "S"."T" RENAME TO "U"`, which the planner emits.
  if (edit.op === 'move-table-schema') synthesizeMoveTableSchema(ctx.engine, edit.fromTable, edit.toSchema);
  const declaration = declarationNameEdit(ctx, edit);
  if (declaration) return [declaration];
  const appended = edit.op === 'rename-table'
    ? synthesizeRenameTable(ctx.engine, edit.fromTable, edit.toName, edit.mysqlToSchema)
    : synthesizeMoveTableSchema(ctx.engine, edit.fromTable, edit.toSchema);

  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: `${sep}${appended};${trailSep === '' ? '' : '\n'}`,
    rationale: edit.op,
  }];
}

/** The offset of the last identifier token of a (possibly qualified) raw
 *  name, quote-aware — `"a.b"` and `[my.table]` are one token. */
function lastIdentifierStart(raw: string): number {
  const token = /"(?:[^"]|"")+"|\[(?:[^\]]|\]\])+\]|`(?:[^`]|``)+`|[^\s."`[\]]+/y;
  let at = 0;
  let last = 0;
  for (;;) {
    token.lastIndex = at;
    const match = token.exec(raw);
    if (!match) break;
    last = match.index;
    at = token.lastIndex;
    const dot = /^\s*\.\s*/.exec(raw.slice(at));
    if (!dot) break;
    at += dot[0].length;
  }
  return last;
}

/** The in-place rewrite of a `CREATE TABLE` declaration's name — when the
 *  source statement declares the very table the edit names and its name token
 *  is where the parse says it is; undefined otherwise (the append path). */
function declarationNameEdit(ctx: DdlEditCtx, edit: TableLifecycleEdit): PlannedByteEdit | undefined {
  const structure = ctx.structure;
  if (structure.kind !== 'create-table') return undefined;
  const declared = structure.table;
  // The designer hands back the structure's own name, so an exact match is
  // the rule; the folded forms are accepted for callers that built the name.
  const same = (a: string | undefined, b: string | undefined) => a !== undefined && a === b;
  if (!same(declared.name, edit.fromTable.name) && !same(declared.foldedName, edit.fromTable.foldedName)) return undefined;
  if (declared.schema && edit.fromTable.schema && !same(declared.schema, edit.fromTable.schema)
    && !same(declared.foldedSchema, edit.fromTable.foldedSchema)) return undefined;
  const raw = declared.raw;
  if (!raw) return undefined;
  const start = ctx.statementInfo.range.startOffset;
  const end = ctx.statementInfo.range.endOffset;
  const statement = ctx.source.slice(start, end);
  // Leading comments are skipped (`/* TABLE t … */ CREATE TABLE t` must not
  // match inside the comment), then the first `TABLE` head word.
  const lead = /^(?:\s*(?:--[^\n]*(?:\n|$)|\/\*[\s\S]*?\*\/))*/.exec(statement)?.[0].length ?? 0;
  const head = /\bTABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?/i.exec(statement.slice(lead));
  if (!head) return undefined;
  const at = start + lead + head.index + head[0].length;
  if (!ctx.source.startsWith(raw, at)) return undefined;
  const nameStart = lastIdentifierStart(raw);
  const nameToken = raw.slice(nameStart);
  const schemaText = raw.slice(0, nameStart);
  let replacement: string;
  // The declaration's quoting style, or quotes when the new name needs them.
  const quote = (name: string) => maybeQuoteIdent(ctx.engine, name, declared.quoted || needsQuoting(name));
  if (edit.op === 'rename-table') {
    replacement = edit.mysqlToSchema !== undefined
      ? `${quote(edit.mysqlToSchema)}.${quote(edit.toName)}`
      : `${schemaText}${quote(edit.toName)}`;
  } else {
    replacement = `${quote(edit.toSchema)}.${nameToken}`;
  }
  return { range: rangeFromOffsets(ctx.lineIdx, at, at + raw.length), replacement, rationale: edit.op };
}
